import { useMemo } from "react";
import { useSearchParams } from "react-router";
import InfiniteLoader from "react-window-infinite-loader";
import { SortDescriptor } from "react-aria-components";

import { getSortOptionFromURI } from "components/Filters/helpers";
import { SortOption } from "components/Filters/types";
import ListEntitiesNew from "components/ListEntitiesNew";
import PaginationIndicator from "components/PaginationIndicator";
import { getSearchQuery } from "components/SearchInput/helpers";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import {
  SearchQueryOrderDirection,
  WorkerPoolScanReference,
  WorkerPoolUser,
} from "types/generated";
import PrivateWorkersTierInfo from "views/Account/PrivateWorkersTierInfo";

import {
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "./constants";
import WorkerPoolUsedByEmpty from "./Empty";
import WorkerPoolUsedByFiltersLayout from "./FiltersLayout";
import WorkerPoolUsedByNoResults from "./HasNoResults";
import {
  alignEntityRowsForMerge,
  mergeSortedUsedByRows,
  workerPoolScanToUsedByRow,
  workerPoolUserToUsedByRow,
} from "./helpers";
import WorkerPoolUsedByVirtualizedListItem from "./ListItem/Virtualized";
import { useFilteredScans } from "./useFilteredScans";
import { useSettledWhileLoading } from "./useSettledWhileLoading";

type WorkerPoolUsedByViewProps = {
  entities: WorkerPoolUser[];
  loadMoreEntities: () => Promise<void>;
  isPageEmpty: boolean;
  totalUsersCount: number;
  loadingIndication: boolean;
  // True when entities is still built from stale (previousData) data for the current sort/
  // search variables. Defaults to false for callers without scans to merge in (e.g. Public),
  // where there's nothing to freeze against anyway.
  isAwaitingFreshData?: boolean;
  hasNoFilteringResults: boolean;
  sortOptions: SortOption[];
  isPublic: boolean;
  scans?: WorkerPoolScanReference[];
};

const WorkerPoolUsedByView = ({
  entities,
  loadMoreEntities,
  isPageEmpty,
  totalUsersCount,
  loadingIndication,
  isAwaitingFreshData = false,
  hasNoFilteringResults,
  sortOptions,
  isPublic,
  scans = [],
}: WorkerPoolUsedByViewProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = getSearchQuery(searchParams);

  const sortDescriptor: SortDescriptor = useMemo(() => {
    const [sortOption] = getSortOptionFromURI(
      searchParams,
      INITIAL_SORT_OPTION,
      INITIAL_SORT_DIRECTION
    );
    return {
      column: sortOption.field,
      direction:
        sortOption.direction === SearchQueryOrderDirection.Asc ? "ascending" : "descending",
    };
  }, [searchParams]);

  const currentParams = useMemo(
    () => ({ sortDescriptor, searchQuery }),
    [sortDescriptor, searchQuery]
  );

  const settledParams = useSettledWhileLoading(currentParams, isAwaitingFreshData);

  const scanRows = useMemo(() => scans.map(workerPoolScanToUsedByRow), [scans]);
  const filteredScanRows = useFilteredScans(
    scanRows,
    settledParams.sortDescriptor,
    settledParams.searchQuery
  );

  const entityRows = useMemo(() => entities.map(workerPoolUserToUsedByRow), [entities]);

  const entityRowsForMerge = useMemo(
    () => alignEntityRowsForMerge(entityRows, settledParams.sortDescriptor),
    [entityRows, settledParams.sortDescriptor]
  );

  const mergedRows = useMemo(
    () => mergeSortedUsedByRows(entityRowsForMerge, filteredScanRows, settledParams.sortDescriptor),
    [entityRowsForMerge, filteredScanRows, settledParams.sortDescriptor]
  );

  const isPageReallyEmpty = isPageEmpty && scans.length === 0;
  const isSearchWithNoResults = hasNoFilteringResults && filteredScanRows.length === 0;
  const currentCount = mergedRows.length;
  const totalCount = totalUsersCount + scans.length;

  const isItemLoaded = (value: number) => value < mergedRows.length;

  return (
    <>
      <PageInfoHeader title="Used by">
        {!isPageReallyEmpty && (
          <Box direction="row" align="center" gap="0 large">
            <PaginationIndicator
              currentCount={currentCount}
              totalCount={totalCount}
              loading={loadingIndication}
              minimumLoadingDuration={200}
            />

            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.WORKER_POOL_USED_BY}
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
          </Box>
        )}
      </PageInfoHeader>

      {!isPublic && <PrivateWorkersTierInfo />}

      {!isPageReallyEmpty && <WorkerPoolUsedByFiltersLayout sortOptions={sortOptions} />}

      {isPageReallyEmpty && <WorkerPoolUsedByEmpty />}

      {isSearchWithNoResults && <WorkerPoolUsedByNoResults />}

      {mergedRows.length > 0 && (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={totalUsersCount + filteredScanRows.length}
          loadMoreItems={loadMoreEntities}
        >
          {({ onItemsRendered }) => (
            <ListEntitiesNew
              itemCount={mergedRows.length}
              itemProps={{
                items: mergedRows,
              }}
              virtualizedItem={WorkerPoolUsedByVirtualizedListItem}
              itemKey={(index) => mergedRows[index].id}
              onItemsRendered={onItemsRendered}
            />
          )}
        </InfiniteLoader>
      )}
    </>
  );
};

export default WorkerPoolUsedByView;
